import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { UpdateMarkingFormDataInputDto } from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import {
    createMarkingFormData,
    getMarkingFormData,
    updateMarkingFormData,
} from './ord.service'

import { apiQueryKey } from '../query-key'
import { type OmitWwld } from '../utils'

export function useGetMarkingFormData(
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<UseQueryOptions<ORDMarkingFormData, Error>, 'queryKey'>
) {
    return useQuery<ORDMarkingFormData, Error>({
        queryKey: apiQueryKey.markingFormData(wwld),
        queryFn: () => getMarkingFormData(wwld),
        // Принудительно обновляем данные при монтировании, чтобы загрузить настройки из CreativeMarkingSettings
        // для новых ссылок с существующими креативами
        refetchOnMount: 'always',
        // Данные всегда считаются устаревшими - важно при смене креатива
        staleTime: 0,
        ...options,
    })
}

/**
 * Проверяет, есть ли изменения в полях параметров редактора маркировки медиа
 * НЕ включает mark_media (чекбокс) и mark_media_color (старый цвет), так как это индивидуальные настройки для каждой ссылки
 */
function hasMediaMarkingChanges(
    input: Partial<UpdateMarkingFormDataInputDto>
): boolean {
    const mediaMarkingFields = [
        // Параметры редактора медиа (синхронизируются)
        'mark_media_text',
        'mark_media_font_size',
        'mark_media_alignment',
        'mark_media_font_family',
        'mark_media_has_background',
        'mark_media_background_color',
        'mark_media_position_x',
        'mark_media_position_y',
        'mark_media_canvas_width',
        'mark_media_canvas_height',
        'mark_media_original_width',
        'mark_media_original_height',
        // Кастомные цвета для палитры (синхронизируются)
        'mark_media_custom_text_colors',
        'mark_media_custom_background_colors',
        // mark_media и mark_media_color НЕ синхронизируются - это индивидуальные настройки
    ] as const

    return mediaMarkingFields.some((field) => input[field] !== undefined)
}

export function useUpdateMarkingFormData(
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<
            ORDMarkingFormData,
            Error,
            OmitWwld<UpdateMarkingFormDataInputDto>,
            {
                ordMarkingFormDataBackup?: ORDMarkingFormData
            }
        >,
        'mutationFn'
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        ORDMarkingFormData,
        Error,
        OmitWwld<UpdateMarkingFormDataInputDto>,
        {
            ordMarkingFormDataBackup?: ORDMarkingFormData
        }
    >({
        mutationFn: (input) => updateMarkingFormData({ ...wwld, ...input }),
        ...options,
        onSuccess: (_, input, context) => {
            // Инвалидируем кэш для текущей ссылки
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })

            // Если были изменены поля маркировки медиа, инвалидируем кэш для всех ссылок
            // в том же workspace, чтобы обновить данные для всех ссылок с тем же креативом
            // (бэкенд синхронизирует данные автоматически)
            if (hasMediaMarkingChanges(input)) {
                // Инвалидируем все запросы markingFormData в том же workspace
                // Это обеспечит обновление данных для всех ссылок с тем же креативом
                queryClient.invalidateQueries({
                    queryKey: apiQueryKey.markingFormData({
                        workspace_type: wwld.workspace_type,
                        workspace_id: wwld.workspace_id,
                    }),
                })
            }

            if (options?.onSuccess) {
                options.onSuccess(_, input, context)
            }
        },
    })
}

export function useCreateMarkingFormData(wwld: WorkspaceWithLinkDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        ORDMarkingFormData,
        Error,
        OmitWwld<UpdateMarkingFormDataInputDto>
    >({
        mutationFn: (input) => createMarkingFormData({ ...wwld, ...input }),
        onSuccess: (data) => {
            // Обновляем кэш сразу с полученными данными
            queryClient.setQueryData(
                apiQueryKey.markingFormData(wwld),
                data
            )

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })
        },
    })
}
